import React, { useState, useEffect, useContext } from 'react';
import { Card, Statistic, Table, Tag, Spin, Empty, Row, Col, Progress } from 'antd';
import {
  IndianRupee,
  TrendingUp,
  Target,
  Handshake,
  UserPlus,
  Phone,
  Mail,
  FileText,
  StickyNote,
  AlertTriangle,
} from 'lucide-react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import useApiMethods from '../../lib/useApiMethods';
import {
  CRM_DATA, CRM_RECEIVABLES, CRM_SALES_LEADERBOARD, CRM_SALES_EXTRAS, TRENDS,
} from '../../constants/api';
import ExportButton from '../../components/common/ExportButton';
import { MetricCard, HeadlineCard, Surface } from '../../components/insights';
import { AuthContext } from '../../context/AuthContext';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, Tooltip, Legend
);

const formatINR = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

const formatLakhs = (amount) => `₹${(amount / 100000).toFixed(1)}L`;

const SalesOverview = () => {
  const { fetch } = useApiMethods();
  const { hasPermission } = useContext(AuthContext);
  const canSeeFinance = hasPermission('finance_sensitive', 'view');
  const [data, setData] = useState(null);
  const [receivables, setReceivables] = useState(null);
  const [trends, setTrends] = useState({ time_series: [], deltas: {} });
  const [leaderboard, setLeaderboard] = useState({ rows: [], as_of: null });
  const [extras, setExtras] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const calls = [
        fetch(CRM_DATA, {}, setLoading),
        fetch(CRM_RECEIVABLES),
        hasPermission('trends', 'view') ? fetch(TRENDS, { period: 'week' }) : Promise.resolve(null),
        fetch(CRM_SALES_LEADERBOARD),
        fetch(CRM_SALES_EXTRAS),
      ];
      const [res, recRes, trendsRes, lbRes, extrasRes] = await Promise.all(calls);
      if (res?.status !== 'fail') {
        setData(res?.data || res);
      }
      if (recRes?.status === 'success') {
        setReceivables(recRes.data);
      }
      if (trendsRes?.status === 'success') {
        setTrends(trendsRes.data || { time_series: [], deltas: {} });
      }
      if (lbRes?.status === 'success') {
        setLeaderboard(lbRes.data || { rows: [], as_of: null });
      }
      if (extrasRes?.status === 'success') {
        setExtras(extrasRes.data || {});
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Empty description="Unable to load sales data" className="mt-20" />;
  }

  const {
    revenue_this_month = 0,
    pipeline_total = 0,
    win_rate = 0,
    deals_closed_this_week = 0,
    revenue_trend: raw_trend,
    pipeline_by_stage: raw_pipeline,
    top_deals: raw_deals,
    forecast: raw_forecast,
    new_leads: raw_new_leads,
  } = data;

  const new_leads = raw_new_leads || { today: 0, this_week: 0, this_month: 0, stage_count: 0 };

  const rec_summary = receivables?.summary || {};
  const rec_clients = receivables?.clients || [];
  const amc_pipeline = receivables?.amc_pipeline || {};

  const revenue_trend = raw_trend || [];
  const top_deals = raw_deals || [];
  const forecast = raw_forecast || {};
  const forecast_by_month = forecast.forecast_by_month || [];
  const deals_at_risk = forecast.deals_at_risk || [];
  const forecast_summary = forecast.summary || {};

  const pipeline_by_stage = Array.isArray(raw_pipeline)
    ? raw_pipeline
    : raw_pipeline ? Object.entries(raw_pipeline).map(([stage, value]) => ({ stage, value })) : [];

  // Sales extras (parallel-fetched bundle)
  const pipeline_funnel = extras.pipeline_funnel || [];
  const lead_sources = extras.lead_sources?.rows || [];
  const stage_aging = extras.stage_aging || { buckets: {}, stuck_deals: [], threshold: 14 };
  const lost_reasons = extras.lost_reasons || { rows: [], total_count: 0, total_value: 0 };
  const activity_volume = extras.activity_volume || { rows: [], totals_by_type: {}, total_activities: 0, days: 7 };
  const quotations_summary = extras.quotations_summary || {
    counts_by_status: {}, sent_in_window: 0, accepted_in_window: 0, acceptance_rate: 0,
    expired_followups: [], pending_approval: 0, days: 30,
  };
  const bookings_trend_12m = extras.bookings_trend_12m || [];

  // Revenue Trend Line Chart
  const revenueTrendData = {
    labels: revenue_trend.map((r) => r.month),
    datasets: [
      {
        label: 'Revenue',
        data: revenue_trend.map((r) => r.revenue),
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: '#6366f1',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const revenueTrendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` Revenue: ${formatINR(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        ticks: { callback: (val) => formatLakhs(val) },
        grid: { color: 'rgba(148,163,184,0.18)' },
      },
    },
  };

  // Pipeline by Stage horizontal Bar Chart
  const stageColors = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'];
  const pipelineChartData = {
    labels: pipeline_by_stage.map((s) => s.stage),
    datasets: [
      {
        label: 'Pipeline Value',
        data: pipeline_by_stage.map((s) => s.value),
        backgroundColor: stageColors.slice(0, pipeline_by_stage.length),
        borderRadius: 6,
        barThickness: 28,
      },
    ],
  };

  const pipelineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` Value: ${formatINR(ctx.parsed.x)}`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { callback: (val) => formatLakhs(val) },
        grid: { color: 'rgba(148,163,184,0.18)' },
      },
      y: { grid: { display: false } },
    },
  };

  const stageTagColors = {
    discovery: 'blue',
    qualification: 'cyan',
    proposal: 'purple',
    negotiation: 'orange',
    closed_won: 'green',
    closed_lost: 'red',
  };

  const dealColumns = [
    {
      title: 'Deal Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span className="font-medium">{text}</span>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (val) => formatINR(val),
      align: 'right',
      sorter: (a, b) => a.value - b.value,
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      key: 'stage',
      render: (stage) => (
        <Tag color={stageTagColors[stage] || 'default'}>
          {stage?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
        </Tag>
      ),
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
    },
  ];

  // Helper: sparkline + delta for a metric from trends
  const seriesFor = (key) => trends.time_series?.length
    ? trends.time_series.map((t) => t[key] || 0)
    : null;
  const deltaFor = (key) => trends.deltas?.[key]?.delta_pct ?? null;

  // Sales headline summary
  const outstandingPct = rec_summary.total_clients
    ? Math.round((rec_summary.clients_with_pending || 0) / rec_summary.total_clients * 100)
    : 0;
  const headlineState = (deals_at_risk.length > 3 || (rec_summary.amc_overdue_count || 0) > 0)
    ? 'critical'
    : (deals_at_risk.length > 0 || outstandingPct > 30)
      ? 'warning'
      : 'healthy';
  const headlineTitle = deals_at_risk.length > 0
    ? `${deals_at_risk.length} deal${deals_at_risk.length > 1 ? 's' : ''} at risk · ${formatINR(deals_at_risk.reduce((s, d) => s + (d.value || 0), 0))} exposure`
    : (rec_summary.amc_overdue_count || 0) > 0
      ? `${rec_summary.amc_overdue_count} AMC${rec_summary.amc_overdue_count > 1 ? 's' : ''} overdue — renewals needed`
      : `Pipeline healthy · ${formatLakhs(pipeline_total)} across ${top_deals.length} open deals`;

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen text-foreground">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Sales</h2>
        <p className="text-sm text-muted-foreground">Pipeline, revenue, and client health</p>
      </div>

      {/* Headline */}
      <HeadlineCard
        state={headlineState}
        title={headlineTitle}
        description={`${new_leads.this_week} new leads this week · ${formatLakhs(revenue_this_month)} revenue this month · ${win_rate}% win rate`}
        metrics={[
          { label: 'New Leads (wk)', value: new_leads.this_week, hint: `${new_leads.today} today` },
          ...(canSeeFinance ? [
            { label: 'Revenue', value: formatLakhs(revenue_this_month) },
            { label: 'Pipeline', value: formatLakhs(pipeline_total) },
            { label: 'Outstanding', value: formatLakhs(rec_summary.outstanding_total || 0), hint: `${rec_summary.clients_with_pending || 0} clients` },
          ] : []),
        ]}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          icon={UserPlus}
          intent="info"
          title="New Leads This Week"
          value={new_leads.this_week}
          subtitle={`${new_leads.today} today · ${new_leads.this_month} this month`}
          sparkline={seriesFor('new_leads_this_week')}
          delta={deltaFor('new_leads_this_week')}
          goodUp={true}
        />
        <MetricCard
          icon={IndianRupee}
          intent="positive"
          title="Bookings This Month"
          value={formatLakhs(revenue_this_month)}
          subtitle="Closed Won contract value"
          sparkline={seriesFor('bookings_this_month') || seriesFor('revenue_this_month')}
          delta={deltaFor('bookings_this_month') ?? deltaFor('revenue_this_month')}
          goodUp={true}
        />
        {canSeeFinance && (
        <MetricCard
          icon={IndianRupee}
          intent="info"
          title="Cash Collected"
          value={formatLakhs((data?.cash_collected_this_month) || 0)}
          subtitle={`${data?.cash_receipt_count_this_month || 0} receipts`}
          sparkline={seriesFor('cash_collected_this_month')}
          delta={deltaFor('cash_collected_this_month')}
          goodUp={true}
        />
        )}
        <MetricCard
          icon={TrendingUp}
          intent="primary"
          title="Pipeline Total"
          value={formatLakhs(pipeline_total)}
          sparkline={seriesFor('pipeline_total')}
          delta={deltaFor('pipeline_total')}
          goodUp={true}
        />
        <MetricCard
          icon={Target}
          intent={win_rate < 20 ? 'negative' : 'warning'}
          title="Win Rate"
          value={`${win_rate}%`}
          sparkline={seriesFor('win_rate')}
          delta={deltaFor('win_rate')}
          goodUp={true}
        />
        <MetricCard
          icon={Handshake}
          intent="info"
          title="Deals Closed This Week"
          value={deals_closed_this_week}
          sparkline={seriesFor('deals_closed_this_week')}
          delta={deltaFor('deals_closed_this_week')}
          goodUp={true}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Revenue Trend</h4>
          {revenue_trend.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ height: 300 }}><Line data={revenueTrendData} options={revenueTrendOptions} /></div>
          )}
        </div>
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <h4 className="font-semibold text-foreground mb-4">Pipeline by Stage</h4>
          {pipeline_by_stage.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
            <div style={{ height: 300 }}><Bar data={pipelineChartData} options={pipelineChartOptions} /></div>
          )}
        </div>
      </div>

      {/* Conversion Funnel */}
      {pipeline_funnel.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="mb-4">
            <h4 className="font-semibold text-foreground">Conversion Funnel</h4>
            <span className="text-sm text-muted-foreground">Lead → Closed Won. Drop-off % between stages.</span>
          </div>
          {(() => {
            const maxCount = Math.max(...pipeline_funnel.map((s) => s.count), 1);
            return (
              <div className="space-y-2">
                {pipeline_funnel.map((s, idx) => {
                  const widthPct = (s.count / maxCount) * 100;
                  const prev = idx > 0 ? pipeline_funnel[idx - 1] : null;
                  const dropoffPct = prev && prev.count > 0
                    ? Math.round((1 - s.count / prev.count) * 100)
                    : null;
                  const stageColor = idx === pipeline_funnel.length - 1
                    ? 'from-emerald-500 to-emerald-600'
                    : 'from-indigo-400 to-indigo-600';
                  return (
                    <div key={s.stage} className="flex items-center gap-3">
                      <div className="w-32 shrink-0 text-sm font-medium text-foreground text-right pr-2">
                        {s.stage}
                      </div>
                      <div className="flex-1 relative">
                        <div className="h-9 bg-muted/40 rounded-md overflow-hidden">
                          <div
                            className={`h-full bg-gradient-to-r ${stageColor} flex items-center px-3 text-white text-sm font-medium transition-all`}
                            style={{ width: `${Math.max(widthPct, 8)}%`, minWidth: '64px' }}
                          >
                            {s.count} {canSeeFinance && s.value > 0 ? `· ${formatLakhs(s.value)}` : ''}
                          </div>
                        </div>
                      </div>
                      <div className="w-24 shrink-0 text-right text-xs">
                        {dropoffPct != null && (
                          <span className={dropoffPct >= 50 ? 'text-rose-600 dark:text-rose-400' : dropoffPct >= 25 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}>
                            {dropoffPct >= 0 ? `−${dropoffPct}%` : `+${Math.abs(dropoffPct)}%`} drop
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Forecast */}
      <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
        <h4 className="font-semibold text-foreground mb-4">Revenue Forecast</h4>
        {forecast_by_month.length === 0 ? <Empty description="No forecast data" image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
          <>
            <div style={{ height: 300 }}>
              <Bar
                data={{
                  labels: forecast_by_month.map((m) => m.month),
                  datasets: [
                    { label: 'Pipeline', data: forecast_by_month.map((m) => m.total_value), backgroundColor: 'rgba(99,102,241,0.2)', borderColor: '#6366f1', borderWidth: 1, borderRadius: 6 },
                    { label: 'Weighted', data: forecast_by_month.map((m) => m.weighted_value), backgroundColor: 'rgba(16,185,129,0.6)', borderColor: '#10b981', borderWidth: 1, borderRadius: 6 },
                  ],
                }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top', labels: { boxWidth: 10, font: { size: 11 } } } }, scales: { y: { ticks: { callback: (v) => `₹${(v/100000).toFixed(0)}L` }, grid: { color: 'rgba(148,163,184,0.15)' } }, x: { grid: { display: false } } } }}
              />
            </div>
            {forecast_summary.total_pipeline > 0 && (
              <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                <div><span className="text-xs text-muted-foreground">Total Pipeline</span><div className="text-lg font-bold text-foreground">{formatINR(forecast_summary.total_pipeline)}</div></div>
                <div><span className="text-xs text-muted-foreground">Weighted Forecast</span><div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatINR(forecast_summary.total_weighted)}</div></div>
                <div><span className="text-xs text-muted-foreground">At Risk</span><div className="text-lg font-bold text-red-500">{forecast_summary.deals_at_risk_count} deals</div></div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bookings Trend (12mo) */}
      {canSeeFinance && bookings_trend_12m.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="mb-4">
            <h4 className="font-semibold text-foreground">Bookings — Last 12 Months</h4>
            <span className="text-sm text-muted-foreground">Monthly Closed-Won contract value</span>
          </div>
          <div style={{ height: 280 }}>
            <Bar
              data={{
                labels: bookings_trend_12m.map((r) => r.month),
                datasets: [{
                  label: 'Bookings',
                  data: bookings_trend_12m.map((r) => r.revenue),
                  backgroundColor: bookings_trend_12m.map((_, i) =>
                    i === bookings_trend_12m.length - 1 ? '#10b981' : 'rgba(99,102,241,0.7)'
                  ),
                  borderRadius: 6,
                }],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: { callbacks: { label: (ctx) => ` ${formatINR(ctx.parsed.y)}` } },
                },
                scales: {
                  x: { grid: { display: false } },
                  y: {
                    beginAtZero: true,
                    ticks: { callback: (v) => formatLakhs(v) },
                    grid: { color: 'rgba(148,163,184,0.15)' },
                  },
                },
              }}
            />
          </div>
        </div>
      )}

      {/* Stage Aging / Stuck Deals */}
      {(stage_aging.stuck_deals.length > 0 || Object.values(stage_aging.buckets).some((v) => v > 0)) && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-foreground">Stage Aging</h4>
              <span className="text-sm text-muted-foreground">
                Days since last stage transition · {stage_aging.stuck_deals.length} stuck ≥ {stage_aging.threshold}d
              </span>
            </div>
          </div>
          <Row gutter={12} className="mb-4">
            {[
              { key: 'fresh', label: '< 7d', color: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300' },
              { key: 'warm', label: '7–13d', color: 'bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-500/30 text-sky-700 dark:text-sky-300' },
              { key: 'cold', label: '14–29d', color: 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 text-yellow-700' },
              { key: 'stale', label: '30–59d', color: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-300' },
              { key: 'frozen', label: '≥ 60d', color: 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300' },
            ].map((b) => (
              <Col xs={12} sm={8} md={Math.floor(24 / 5)} key={b.key} style={{ marginBottom: 8 }}>
                <div className={`rounded-xl border p-3 text-center ${b.color}`}>
                  <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{b.label}</div>
                  <div className="text-2xl font-bold mt-1">{stage_aging.buckets[b.key] || 0}</div>
                </div>
              </Col>
            ))}
          </Row>
          {stage_aging.stuck_deals.length > 0 && (
            <Table
              dataSource={stage_aging.stuck_deals}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 10 }}
              columns={[
                { title: 'Deal', dataIndex: 'name', key: 'name', render: (v) => <span className="font-medium">{v}</span> },
                { title: 'Stage', dataIndex: 'stage', key: 'stage', render: (v) => <Tag color={stageTagColors[(v || '').toLowerCase().replace(/ /g, '_')] || 'default'}>{v}</Tag> },
                { title: 'Owner', dataIndex: 'owner', key: 'owner' },
                ...(canSeeFinance ? [{ title: 'Value', dataIndex: 'value', key: 'value', align: 'right', render: (v) => v > 0 ? formatLakhs(v) : '—' }] : []),
                {
                  title: 'Days Stuck', dataIndex: 'days_stuck', key: 'days_stuck', align: 'right',
                  sorter: (a, b) => a.days_stuck - b.days_stuck,
                  defaultSortOrder: 'descend',
                  render: (v) => {
                    const cls = v >= 60 ? 'text-rose-600 dark:text-rose-400 font-bold'
                      : v >= 30 ? 'text-amber-600 dark:text-amber-400 font-semibold'
                      : 'text-foreground';
                    return <span className={cls}>{v}d</span>;
                  },
                },
              ]}
            />
          )}
        </div>
      )}

      {/* Deals at Risk */}
      {deals_at_risk.length > 0 && (
        <div className="rounded-2xl bg-card border border-rose-200 dark:border-rose-500/30 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-rose-600 dark:text-rose-400">Deals at Risk</h4>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">{deals_at_risk.length} deals</span>
              <span className="text-sm font-semibold text-rose-600 dark:text-rose-400">
                {formatINR(deals_at_risk.reduce((s, d) => s + (d.value || 0), 0))} exposure
              </span>
            </div>
          </div>
          <div className="space-y-2">
            {deals_at_risk.map((d) => (
              <div
                key={d.id}
                className="relative flex items-center gap-4 p-3 rounded-xl border border-rose-200/60 dark:border-rose-500/20 bg-gradient-to-r from-rose-50/80 via-rose-50/40 to-transparent dark:from-rose-950/40 dark:via-rose-950/20 dark:to-transparent overflow-hidden"
              >
                <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-rose-400 to-rose-600 rounded-l-xl" />
                <div className="flex-1 min-w-0 pl-1">
                  <div className="font-semibold text-foreground truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{d.owner} · {d.stage}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold text-foreground">{formatINR(d.value)}</div>
                  <span className="inline-block mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200/60 dark:border-rose-500/20">
                    {d.days_overdue}d overdue
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Deals */}
      <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-foreground">Proposal & Negotiation Deals</h4>
            <span className="text-sm text-muted-foreground">{top_deals.length} deals · {formatINR(top_deals.reduce((s, d) => s + (d.value || 0), 0))} total value</span>
          </div>
          <ExportButton
            data={top_deals}
            filename="deals-proposal-negotiation"
            pdfColumns={[{title:'Deal',dataKey:'name'},{title:'Value',dataKey:'value'},{title:'Stage',dataKey:'stage'},{title:'Owner',dataKey:'owner'},{title:'Expected Close',dataKey:'expected_close'}]}
            pdfTitle="Proposal & Negotiation Deals"
          />
        </div>
        <Table
          dataSource={top_deals}
          columns={[...dealColumns, { title: 'Expected Close', dataIndex: 'expected_close', key: 'expected_close', render: (d) => d || '-' }]}
          rowKey={(record) => record.id || record.name}
          pagination={{ pageSize: 10 }}
          size="middle"
          locale={{ emptyText: <Empty description="No deals" /> }}
        />
      </div>

      {/* Sales Rep Leaderboard */}
      {leaderboard.rows.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-foreground">Sales Rep Leaderboard</h4>
              <span className="text-sm text-muted-foreground">
                FY-to-date · {leaderboard.rows.length} reps · sorted by won value
              </span>
            </div>
            <ExportButton
              data={leaderboard.rows}
              filename="sales-leaderboard"
              pdfColumns={[
                { title: 'Rep', dataKey: 'name' },
                { title: 'Active Leads', dataKey: 'leads_active' },
                { title: 'Won (FY)', dataKey: 'deals_won_fy' },
                { title: 'Won Value (FY)', dataKey: 'won_value_fy' },
                { title: 'Bookings (Mo)', dataKey: 'bookings_this_month' },
                { title: 'Pipeline', dataKey: 'pipeline_value' },
                { title: 'Win %', dataKey: 'win_rate' },
                { title: 'Avg Deal', dataKey: 'avg_deal_size' },
              ]}
              pdfTitle="Sales Rep Leaderboard"
            />
          </div>
          <Table
            dataSource={leaderboard.rows}
            rowKey="id"
            size="middle"
            pagination={false}
            columns={[
              {
                title: 'Rep',
                dataIndex: 'name',
                key: 'name',
                render: (v, _r, idx) => (
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                      idx === 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300'
                      : idx === 1 ? 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                      : idx === 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300'
                      : 'bg-muted text-muted-foreground'
                    }`}>{idx + 1}</span>
                    <span className="font-medium text-foreground">{v}</span>
                  </div>
                ),
              },
              {
                title: 'Active',
                dataIndex: 'leads_active',
                key: 'leads_active',
                align: 'right',
                sorter: (a, b) => a.leads_active - b.leads_active,
              },
              {
                title: 'Won (FY)',
                dataIndex: 'deals_won_fy',
                key: 'deals_won_fy',
                align: 'right',
                sorter: (a, b) => a.deals_won_fy - b.deals_won_fy,
                render: (v) => <span className="font-medium">{v}</span>,
              },
              ...(canSeeFinance ? [{
                title: 'Won Value (FY)',
                dataIndex: 'won_value_fy',
                key: 'won_value_fy',
                align: 'right',
                sorter: (a, b) => a.won_value_fy - b.won_value_fy,
                defaultSortOrder: 'descend',
                render: (v) => <span className="font-semibold text-emerald-600 dark:text-emerald-400">{formatLakhs(v)}</span>,
              }, {
                title: 'Bookings (Mo)',
                dataIndex: 'bookings_this_month',
                key: 'bookings_this_month',
                align: 'right',
                sorter: (a, b) => a.bookings_this_month - b.bookings_this_month,
                render: (v) => v > 0 ? formatLakhs(v) : <span className="text-muted-foreground">—</span>,
              }, {
                title: 'Pipeline',
                dataIndex: 'pipeline_value',
                key: 'pipeline_value',
                align: 'right',
                sorter: (a, b) => a.pipeline_value - b.pipeline_value,
                render: (v) => v > 0 ? formatLakhs(v) : <span className="text-muted-foreground">—</span>,
              }] : []),
              {
                title: 'Win %',
                dataIndex: 'win_rate',
                key: 'win_rate',
                align: 'right',
                sorter: (a, b) => a.win_rate - b.win_rate,
                render: (v) => {
                  const color = v >= 40 ? 'text-emerald-600 dark:text-emerald-400'
                    : v >= 20 ? 'text-amber-600 dark:text-amber-400'
                    : v > 0 ? 'text-rose-600 dark:text-rose-400'
                    : 'text-muted-foreground';
                  return <span className={`font-medium ${color}`}>{v ? `${v}%` : '—'}</span>;
                },
              },
              ...(canSeeFinance ? [{
                title: 'Avg Deal',
                dataIndex: 'avg_deal_size',
                key: 'avg_deal_size',
                align: 'right',
                sorter: (a, b) => a.avg_deal_size - b.avg_deal_size,
                render: (v) => v > 0 ? formatLakhs(v) : <span className="text-muted-foreground">—</span>,
              }] : []),
            ]}
          />
        </div>
      )}

      {/* Lead Source Breakdown */}
      {lead_sources.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="rounded-2xl bg-card border border-border shadow-sm p-5 lg:col-span-1">
            <h4 className="font-semibold text-foreground mb-4">Lead Sources</h4>
            <div style={{ height: 260 }}>
              <Doughnut
                data={{
                  labels: lead_sources.slice(0, 8).map((s) => s.source),
                  datasets: [{
                    data: lead_sources.slice(0, 8).map((s) => s.lead_count),
                    backgroundColor: ['#6366f1', '#8b5cf6', '#a78bfa', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'],
                    borderWidth: 0,
                  }],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => ` ${ctx.label}: ${ctx.parsed} leads`,
                      },
                    },
                  },
                  cutout: '60%',
                }}
              />
            </div>
          </div>
          <div className="rounded-2xl bg-card border border-border shadow-sm p-5 lg:col-span-2">
            <h4 className="font-semibold text-foreground mb-4">Source Conversion (FY)</h4>
            <Table
              dataSource={lead_sources}
              rowKey="source"
              size="small"
              pagination={{ pageSize: 6 }}
              columns={[
                { title: 'Source', dataIndex: 'source', key: 'source', render: (v) => <span className="font-medium">{v}</span> },
                { title: 'Leads', dataIndex: 'lead_count', key: 'lead_count', align: 'right', sorter: (a, b) => a.lead_count - b.lead_count },
                { title: 'Won', dataIndex: 'won_count', key: 'won_count', align: 'right', sorter: (a, b) => a.won_count - b.won_count },
                ...(canSeeFinance ? [{ title: 'Won Value', dataIndex: 'won_value', key: 'won_value', align: 'right', render: (v) => v > 0 ? <span className="text-emerald-600 dark:text-emerald-400 font-medium">{formatLakhs(v)}</span> : '—', sorter: (a, b) => a.won_value - b.won_value }] : []),
                {
                  title: 'Conv. %', dataIndex: 'conversion_rate', key: 'conversion_rate', align: 'right',
                  sorter: (a, b) => a.conversion_rate - b.conversion_rate,
                  defaultSortOrder: 'descend',
                  render: (v) => {
                    const cls = v >= 30 ? 'text-emerald-600 dark:text-emerald-400'
                      : v >= 10 ? 'text-amber-600 dark:text-amber-400'
                      : v > 0 ? 'text-rose-600 dark:text-rose-400'
                      : 'text-muted-foreground';
                    return <span className={`font-medium ${cls}`}>{v ? `${v}%` : '—'}</span>;
                  },
                },
              ]}
            />
          </div>
        </div>
      )}

      {/* Activity Volume */}
      {activity_volume.total_activities > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-foreground">Activity Volume</h4>
              <span className="text-sm text-muted-foreground">
                Calls, emails, notes, documents logged in the last {activity_volume.days} days · {activity_volume.total_activities} total
              </span>
            </div>
          </div>
          <Row gutter={12} className="mb-4">
            {[
              { key: 'call', label: 'Calls', icon: Phone, color: 'text-sky-600 dark:text-sky-400' },
              { key: 'email', label: 'Emails', icon: Mail, color: 'text-violet-600 dark:text-violet-400' },
              { key: 'note', label: 'Notes', icon: StickyNote, color: 'text-amber-600 dark:text-amber-400' },
              { key: 'document', label: 'Docs', icon: FileText, color: 'text-emerald-600 dark:text-emerald-400' },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <Col xs={12} sm={6} key={t.key} style={{ marginBottom: 8 }}>
                  <div className="rounded-xl border border-border bg-muted/30 p-3 flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${t.color}`} />
                    <div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider">{t.label}</div>
                      <div className="text-xl font-bold text-foreground">{activity_volume.totals_by_type[t.key] || 0}</div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
          <Table
            dataSource={activity_volume.rows}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 8 }}
            columns={[
              { title: 'Rep', dataIndex: 'name', key: 'name', render: (v) => <span className="font-medium">{v}</span> },
              { title: 'Calls', dataIndex: 'call', key: 'call', align: 'right' },
              { title: 'Emails', dataIndex: 'email', key: 'email', align: 'right' },
              { title: 'Notes', dataIndex: 'note', key: 'note', align: 'right' },
              { title: 'Docs', dataIndex: 'document', key: 'document', align: 'right' },
              {
                title: 'Total', dataIndex: 'total', key: 'total', align: 'right',
                sorter: (a, b) => a.total - b.total, defaultSortOrder: 'descend',
                render: (v) => <span className="font-semibold">{v}</span>,
              },
            ]}
          />
        </div>
      )}

      {/* Quote / Proposal Tracker */}
      {(Object.values(quotations_summary.counts_by_status).some((v) => v > 0) || quotations_summary.expired_followups.length > 0) && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-foreground">Quotation Tracker</h4>
              <span className="text-sm text-muted-foreground">
                Last {quotations_summary.days} days · {quotations_summary.sent_in_window} sent · {quotations_summary.accepted_in_window} accepted · {quotations_summary.acceptance_rate}% acceptance
              </span>
            </div>
            {quotations_summary.pending_approval > 0 && (
              <Tag color="orange" icon={<AlertTriangle className="w-3 h-3 inline mr-1" />}>
                {quotations_summary.pending_approval} pending approval
              </Tag>
            )}
          </div>
          <Row gutter={12} className="mb-4">
            {[
              { key: 'draft', label: 'Draft', color: 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300' },
              { key: 'sent', label: 'Sent', color: 'bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-500/30 text-sky-700 dark:text-sky-300' },
              { key: 'viewed', label: 'Viewed', color: 'bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-500/30 text-violet-700 dark:text-violet-300' },
              { key: 'accepted', label: 'Accepted', color: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300' },
              { key: 'rejected', label: 'Rejected', color: 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300' },
              { key: 'expired', label: 'Expired', color: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-300' },
            ].map((t) => (
              <Col xs={12} sm={8} md={4} key={t.key} style={{ marginBottom: 8 }}>
                <div className={`rounded-xl border p-3 text-center ${t.color}`}>
                  <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{t.label}</div>
                  <div className="text-2xl font-bold mt-1">{quotations_summary.counts_by_status[t.key] || 0}</div>
                </div>
              </Col>
            ))}
          </Row>
          {quotations_summary.expired_followups.length > 0 && (
            <>
              <div className="text-sm font-medium text-foreground mb-2">Expired / needs follow-up ({quotations_summary.expired_followups.length})</div>
              <Table
                dataSource={quotations_summary.expired_followups}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 8 }}
                columns={[
                  { title: 'Quote #', dataIndex: 'quote_number', key: 'quote_number', render: (v) => <span className="font-mono text-xs">{v}</span> },
                  { title: 'Customer', dataIndex: 'customer', key: 'customer', render: (v) => v || '—' },
                  { title: 'Quote Date', dataIndex: 'quote_date', key: 'quote_date', render: (v) => v || '—' },
                  { title: 'Valid Until', dataIndex: 'valid_until', key: 'valid_until', render: (v) => v || '—' },
                  { title: 'Status', dataIndex: 'status', key: 'status', render: (v) => <Tag color={v === 'expired' ? 'orange' : 'blue'}>{v}</Tag> },
                ]}
              />
            </>
          )}
        </div>
      )}

      {/* Client Receivables */}
      {canSeeFinance && rec_clients.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-foreground">Client Receivables</h4>
              <span className="text-sm text-muted-foreground">
                {rec_summary.clients_with_pending || 0} of {rec_summary.total_clients || 0} clients with pending · {formatINR(rec_summary.outstanding_total || 0)} outstanding
              </span>
            </div>
            <ExportButton
              data={rec_clients.map((c) => ({
                org_name: c.org_name,
                product_sold: c.product_sold,
                contract_value: c.contract_value,
                paid: c.paid,
                pending: c.pending,
                onboarding_status: c.onboarding_status,
                amc_status: c.amc?.amc_status || '-',
                amc_end_date: c.amc?.amc_end_date || '-',
              }))}
              filename="client-receivables"
              pdfColumns={[
                { title: 'Client', dataKey: 'org_name' },
                { title: 'Product', dataKey: 'product_sold' },
                { title: 'Billed', dataKey: 'contract_value' },
                { title: 'Paid', dataKey: 'paid' },
                { title: 'Pending', dataKey: 'pending' },
                { title: 'AMC Status', dataKey: 'amc_status' },
                { title: 'AMC End', dataKey: 'amc_end_date' },
              ]}
              pdfTitle="Client Receivables"
            />
          </div>
          <Table
            dataSource={rec_clients}
            columns={[
              { title: 'Client', dataIndex: 'org_name', key: 'org_name', render: (v, r) => (
                <div>
                  <div className="font-medium text-foreground">{v}</div>
                  {r.city && <div className="text-xs text-muted-foreground">{r.city}</div>}
                </div>
              )},
              { title: 'Product', dataIndex: 'product_sold', key: 'product_sold', render: (v) => v || '-' },
              { title: 'Billed', dataIndex: 'contract_value', key: 'contract_value',
                render: (v) => <span className="font-medium">{formatINR(v || 0)}</span>,
                sorter: (a, b) => (a.contract_value || 0) - (b.contract_value || 0) },
              { title: 'Paid', dataIndex: 'paid', key: 'paid',
                render: (v) => <span className="text-emerald-600 dark:text-emerald-400 font-medium">{formatINR(v || 0)}</span>,
                sorter: (a, b) => (a.paid || 0) - (b.paid || 0) },
              { title: 'Pending', dataIndex: 'pending', key: 'pending',
                render: (v) => v > 0
                  ? <span className="text-rose-600 dark:text-rose-400 font-semibold">{formatINR(v)}</span>
                  : <Tag color="green">Paid</Tag>,
                sorter: (a, b) => (a.pending || 0) - (b.pending || 0),
                defaultSortOrder: 'descend' },
              { title: 'AMC', key: 'amc', render: (_, r) => {
                if (!r.amc) return <span className="text-muted-foreground">—</span>;
                const color = r.amc.amc_status === 'Paid' ? 'green'
                  : r.amc.amc_status === 'Overdue' ? 'red' : 'orange';
                return (
                  <div>
                    <Tag color={color}>{r.amc.amc_status}</Tag>
                    {r.amc.amc_end_date && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Ends {r.amc.amc_end_date}
                        {r.amc.amc_days_to_expiry != null && r.amc.amc_days_to_expiry < 60 && (
                          <span className={r.amc.amc_days_to_expiry < 0 ? 'text-rose-600 dark:text-rose-400 ml-1' : 'text-amber-600 dark:text-amber-400 ml-1'}>
                            ({r.amc.amc_days_to_expiry < 0 ? `${Math.abs(r.amc.amc_days_to_expiry)}d overdue` : `${r.amc.amc_days_to_expiry}d`})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              }},
              { title: 'Status', dataIndex: 'onboarding_status', key: 'onboarding_status',
                render: (v) => <Tag>{v}</Tag> },
            ]}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            size="middle"
            locale={{ emptyText: <Empty description="No onboarded clients" /> }}
          />
        </div>
      )}

      {/* Lost-Deal Reasons */}
      {lost_reasons.rows.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-foreground">Lost Deals (FY)</h4>
              <span className="text-sm text-muted-foreground">
                {lost_reasons.total_count} deals · {canSeeFinance ? `${formatINR(lost_reasons.total_value)} value · ` : ''}reason from Closed-Lost remarks
              </span>
            </div>
            <ExportButton
              data={lost_reasons.rows}
              filename="lost-deals"
              pdfColumns={[
                { title: 'Customer', dataKey: 'customer_name' },
                { title: 'Value', dataKey: 'value' },
                { title: 'Lost At', dataKey: 'lost_at' },
                { title: 'Reason', dataKey: 'reason' },
                { title: 'Rep', dataKey: 'rep' },
              ]}
              pdfTitle="Lost Deals (FY)"
            />
          </div>
          <Table
            dataSource={lost_reasons.rows}
            rowKey="customer_id"
            size="small"
            pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name', render: (v) => <span className="font-medium">{v}</span> },
              { title: 'Lost At', dataIndex: 'lost_at', key: 'lost_at', render: (v) => v || '—', sorter: (a, b) => (a.lost_at || '').localeCompare(b.lost_at || ''), defaultSortOrder: 'descend' },
              ...(canSeeFinance ? [{ title: 'Value', dataIndex: 'value', key: 'value', align: 'right', render: (v) => v > 0 ? <span className="text-rose-600 dark:text-rose-400 font-medium">{formatLakhs(v)}</span> : '—', sorter: (a, b) => a.value - b.value }] : []),
              { title: 'Reason', dataIndex: 'reason', key: 'reason', render: (v) => <span className="text-sm">{v}</span> },
              { title: 'Rep', dataIndex: 'rep', key: 'rep', render: (v) => v || '—' },
            ]}
          />
        </div>
      )}

      {/* AMC Pipeline */}
      {canSeeFinance && rec_clients.length > 0 && (
        <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
          <div className="mb-4">
            <h4 className="font-semibold text-foreground">AMC Renewal Pipeline</h4>
            <span className="text-sm text-muted-foreground">Upcoming and overdue AMC renewals</span>
          </div>
          <Row gutter={16}>
            {[
              { key: 'overdue', label: 'Overdue', color: 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300' },
              { key: 'this_month', label: 'This Month', color: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-300' },
              { key: 'next_30', label: 'Next 30 Days', color: 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 text-yellow-700' },
              { key: 'next_60', label: 'Next 60 Days', color: 'bg-sky-50 dark:bg-sky-950/30 border-sky-200 text-sky-700 dark:text-sky-300' },
              { key: 'next_90', label: 'Next 90 Days', color: 'bg-muted border-slate-200 text-slate-700' },
            ].map((bucket) => {
              const items = amc_pipeline[bucket.key] || [];
              const total = items.reduce((s, i) => s + (i.amc_pending || 0), 0);
              return (
                <Col xs={24} sm={12} md={8} lg={Math.floor(24 / 5)} key={bucket.key} style={{ marginBottom: 16 }}>
                  <div className={`rounded-xl border p-4 ${bucket.color}`}>
                    <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{bucket.label}</div>
                    <div className="text-2xl font-bold mt-1">{items.length}</div>
                    <div className="text-xs mt-1">{formatINR(total)} pending</div>
                    {items.length > 0 && (
                      <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
                        {items.slice(0, 5).map((i) => (
                          <div key={i.amc_id} className="text-xs bg-white/50 rounded px-2 py-1">
                            <div className="font-medium truncate">{i.org_name}</div>
                            <div className="opacity-70">{i.amc_end_date} · {formatINR(i.amc_pending)}</div>
                          </div>
                        ))}
                        {items.length > 5 && (
                          <div className="text-[11px] opacity-70 px-2">+{items.length - 5} more</div>
                        )}
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      )}
    </div>
  );
};

export default SalesOverview;
